import { useEffect, RefObject, useRef } from 'react';

interface IUseInfiniteScroll {
  callback: () => void;
  ref: RefObject<HTMLDivElement>;
  isLoading: boolean;
  hasMore: boolean;
  hasError?: boolean | undefined;
  listLength?: number | undefined;
}

const NEAR_BOTTOM_MARGIN_PX = 120;

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) {
    return null;
  }

  let parent = element.parentElement;
  while (parent) {
    const { overflowY, overflow } = window.getComputedStyle(parent);
    const overflowYResolved = overflowY !== 'visible' ? overflowY : overflow;
    if (overflowYResolved === 'auto' || overflowYResolved === 'scroll' || overflowYResolved === 'overlay') {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

const isSentinelNearBottom = (element: HTMLElement, scrollRoot: HTMLElement | null): boolean => {
  const rect = element.getBoundingClientRect();

  if (!scrollRoot) {
    return rect.bottom <= window.innerHeight + NEAR_BOTTOM_MARGIN_PX;
  }

  const rootRect = scrollRoot.getBoundingClientRect();

  return rect.top <= rootRect.bottom + NEAR_BOTTOM_MARGIN_PX && rect.bottom >= rootRect.top;
};

const useInfiniteScroll = ({ callback, ref, isLoading, hasMore, hasError = false, listLength }: IUseInfiniteScroll) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const isTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      isTriggeredRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    if (!hasMore || hasError || isLoading) {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const scrollParent = getScrollParent(element);
    const scrollTarget: HTMLElement | Window = scrollParent ?? window;
    const useDocumentScrollFallback = !scrollParent;

    const check = () => {
      const el = ref.current;
      if (!el || isTriggeredRef.current) {
        return;
      }

      if (!isSentinelNearBottom(el, scrollParent)) {
        return;
      }

      isTriggeredRef.current = true;
      callbackRef.current();
    };

    scrollTarget.addEventListener('scroll', check, { passive: true });
    if (useDocumentScrollFallback) {
      document.addEventListener('scroll', check, { passive: true });
    }
    window.addEventListener('resize', check, { passive: true });

    let rafOuter = 0;
    let rafInner = 0;
    rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(check);
    });

    return () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      scrollTarget.removeEventListener('scroll', check);
      if (useDocumentScrollFallback) {
        document.removeEventListener('scroll', check);
      }
      window.removeEventListener('resize', check);
    };
  }, [hasMore, hasError, isLoading, ref, listLength]);
};

export default useInfiniteScroll;
